import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, EventEmitter, HostBinding, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, SimpleChanges, Type, ViewChild, ViewContainerRef, ViewEncapsulation, AfterViewInit, ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { LandingModelService } from 'projects/common/services/landingModel.service';
import { UpaService, UpaTarget, UpaTrackingData } from 'projects/common/services/upa.service';

export interface SliderProOptions {
    itemsToShow: { [screenType: string]: number };
    itemsToMove: { [screenType: string]: number };
    fixedWidth?: { [screenType: string]: number | undefined };
    dragToMove: { [screenType: string]: boolean };
    showArrows: { [screenType: string]: boolean };
    showDots: { [screenType: string]: boolean };
    containerWidth?: { [screenType: string]: string | undefined };
    dotsLighten?: boolean;
    arrowsInnerContainer?: boolean;
    arrowsSize?: string;
    infiniteScroll?: { [screenType: string]: boolean };
    autoScroll?: { [screenType: string]: boolean };
    autoScrollInterval?: { [screenType: string]: number };
    useExtendedArrows?: { [screenType: string]: boolean };
    arrowsColor?: { [screenType: string]: string | undefined };
}

@Component({
    selector: 'slider-pro',
    template: `
        <div #sliderPro class="slider-pro"
            (mouseenter)="handleMouseEnter()"
            (mouseleave)="handleMouseLeave()">
            <div class="slider-pro-container page-{{currentPage}} move-{{itemsToMove}} show-{{itemsToShow}}"
                [ngClass]="{'last-page': pages.length > 1 && currentPage === (pages.length - 1), 'with-gaps': useGaps}"
                (touchstart)="dragToMove && onTouchStart($event)"
                (touchmove)="dragToMove && onTouchMove($event)"
                (touchend)="dragToMove && onTouchEnd()"
                (mousedown)="dragToMove && onMouseDown($event)"
                (mousemove)="dragToMove && onMouseMove($event)"
                (mouseup)="dragToMove && onMouseUp()"
                >
                <ng-container #sliderRef></ng-container>
            </div>

            <div class="dots-container">
                <ul *ngIf="showDots && pages.length > 1" class="eva-3-nav-dots -center -eva-3-mt-md -carousel"
                    [ngClass]="dotsClasses">
                    <li *ngFor="let page of pages; let i = index" class="nav-dot"
                        [ngClass]="{
                            '-active': currentPage === i,
                            '-active-prev-prev': (currentPage - 2) === i,
                            '-active-prev': (currentPage - 1) === i,
                            '-active-next': (currentPage + 1) === i,
                            '-active-next-next': (currentPage + 2) === i,
                            '-visible': (currentPage - 2) <= i && (currentPage + 2) >= i
                        }"
                    ></li>
                </ul>
            </div>
        </div>

        <div *ngIf="showArrows && canMovePrev"
            [class]="arrowContainerClasses + ' -left'"
            (click)="prev()"
            (mouseenter)="handleMouseEnter()"
            (mouseleave)="handleMouseLeave()">

            <i *ngIf="!useExtendedArrows" class="nav-slider-icon eva-3-icon-chevron-left"></i>
            
            <div *ngIf="!!useExtendedArrows" 
                [class]="arrowClasses + ' -left'">
                <i class="nav-slider-icon eva-3-icon-chevron-left"></i>
            </div>

        </div>

        <div *ngIf="showArrows && canMoveNext"
            [class]="arrowContainerClasses + ' -right'"
            (click)="next()"
            (mouseenter)="handleMouseEnter()"
            (mouseleave)="handleMouseLeave()">

            <i *ngIf="!useExtendedArrows" class="nav-slider-icon eva-3-icon-chevron-right"></i>

            <div *ngIf="!!useExtendedArrows"
                [class]="arrowClasses + ' -right'">
                <i class="nav-slider-icon eva-3-icon-chevron-right"></i>
            </div>

        </div>
    `,
    styleUrls: ['./slider-pro.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class SliderProComponent implements OnInit, OnChanges, AfterContentChecked, OnDestroy {
    private static MARGIN_GAP = 16;
    private static DEFAULT_AUTO_SCROLL_INTERVAL = 3000;
    private static INTERACTION_PAUSE_DURATION = 3000;

    private isInfiniteMode: boolean = false;
    private isHovered: boolean = false;
    private isTouchDevice: boolean = false;
    private hoverTimeoutId: any;
    private originalComponentsLength: number = 0;
    private virtualCurrentIndex: number = 0;
    private autoScrollIntervalId: any;
    private observer: IntersectionObserver;

    @Input() options: SliderProOptions;
    @Input() screenType: 'DESKTOP' | 'TABLET' | 'MOBILE' | 'DEFAULT' = 'DEFAULT';
    @Input() componentParams: {[key: string]: any}[];
    @Input() components: Type<any>[];
    @Input() upaData: UpaTrackingData;
    @Input() viewFullWidth: boolean = false;
    @Input() resizable: boolean = true;
    @Input() useGaps: boolean = true;

    @Output() initialized = new EventEmitter<void>();

    @HostBinding('style.--left-margin-move') leftMarginMove = 0;
    @HostBinding('style.--slider-pro-width') sliderProWidth;
    @HostBinding('style.--item-width-value') itemWidthValue;
    @HostBinding('style.--parcial-row-width') parcialRowWidth;
    @HostBinding('style.--arrow-left-position') arrowLeftPosition;
    @HostBinding('style.--arrow-right-position') arrowRightPosition;

    @HostBinding('class.view-full-width') get isViewFullWidth() {
        return this.viewFullWidth;
    }

    @ViewChild('sliderRef', { read: ViewContainerRef, static: true }) sliderRef: ViewContainerRef;
    @ViewChild('sliderPro', { read: ViewContainerRef, static: true }) sliderPro: ViewContainerRef;

    get dotsClasses(): { [key: string]: boolean } {
        return {
            '-lighten': !!this.options?.dotsLighten,
            '-lg': this.viewFullWidth && this.currentScreenType !== 'MOBILE'
        };
    }

    get showArrows(): boolean {
        if (this.viewFullWidth && this.currentScreenType === 'DESKTOP') {
            return this.isHovered || this.isTouchDevice;
        }
        return !!this.options?.showArrows?.[this.screenType];
    }

    get showDots(): boolean {
        if (this.viewFullWidth && this.currentScreenType === 'DESKTOP') {
            return this.isHovered || this.isTouchDevice;
        }
        return !!this.options?.showDots?.[this.screenType];
    }

    private getIsTouchDevice(): boolean {
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    get canMovePrev(): boolean {
        return this.isInfiniteMode || this.currentPage > 0;
    }

    get canMoveNext(): boolean {
        return this.isInfiniteMode || this.currentPage < (this.pages.length - 1);
    }

    itemsToShow: number;
    itemsToMove: number;
    dragToMove: boolean;
    fixedWidth: number | undefined;
    containerWidth: string | undefined;
    totalItemsToDraw: number;
    totalRowWidth: number;

    maxLeftMarginMove: number;
    componentList: any[] = [];
    currentPage: number = 0;
    pages: any[] = [];
    arrowsClasses: { [key: string]: boolean } = {};
    useExtendedArrows: boolean;
    arrowClasses: string = '';
    arrowContainerClasses: string = '';

    startX: number;
    startY: number;
    actionType: 'next' | 'prev' | null;
    startDrag: boolean;

    currentScreenType: string;
    closestChecked = false;
    currentClientWidth: number;
    lastReinitializeTime: number = 0;

    constructor(@Inject(PLATFORM_ID) private platformId: string,
    private upaService: UpaService,
    private landingModelService: LandingModelService) {
        this.currentScreenType = this.landingModelService.getLandingModel().device.type;
    }

    ngOnInit() {
        this.initializeVariables();

        if (isPlatformServer(this.platformId)) {
            if (this.containerWidth) {
                this.sliderProWidth = this.containerWidth;
            } else if (this.currentScreenType === 'MOBILE') {
                this.sliderProWidth = 414;
            } else if (this.itemsToShow <= 3) {
                this.sliderProWidth = 961;
            } else {
                this.sliderProWidth = 1280;
            }
            this.initialize();
        }
    }

    // TODO revisar - saturaba el navegador
    @HostListener('window:resize') onResize = () => {
        if (this.resizable) {
            const clientWidth = globalThis.innerWidth;
            if (clientWidth != this.currentClientWidth) {
                this.currentClientWidth = clientWidth;
                this.calculateArrowPositions();
                this.reinitialize();
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes?.componentParams?.firstChange && changes?.componentParams?.currentValue != null) {
            this.reinitialize();
        }
    }

    ngAfterContentChecked(): void {
        if (this.currentScreenType === 'MOBILE' || !this.closestChecked) {
            if (this.shouldReinitializeSlider()) {
                this.reinitialize();
                this.closestChecked = true;
            }
        }
    }
    
    ngOnDestroy() {
        this.stopAutoScrollAndCleanup();
    }

    private shouldReinitializeSlider(): boolean {
        const sliderProElementWidth = this.sliderPro.element.nativeElement.closest("slider-pro").clientWidth;
        const minWidthCheck = globalThis.innerWidth ? globalThis.innerWidth / 2 : 150;

        return (!this.sliderProWidth || this.sliderProWidth < minWidthCheck) && sliderProElementWidth;
    }

    reinitialize() {
        const currentTime = Date.now();
        if (currentTime - this.lastReinitializeTime < 1000) return; // Evita que se reinicialice más de una vez por segundo

        this.lastReinitializeTime = currentTime;

        if (this.shouldReinitializeSlider()) {
            const sliderProElementWidth = this.sliderPro.element.nativeElement.closest("slider-pro").clientWidth;
            this.sliderProWidth = sliderProElementWidth < globalThis.innerWidth ? sliderProElementWidth : globalThis.innerWidth;
        }

        this.stopAutoScrollAndCleanup();
        this.clearComponents();
        this.initializeVariables();
        this.initialize();
        this.initialized.emit();
    }

    private clearComponents() {
        this.componentList.forEach(component => {
            if (component) {
                component.destroy();
            }
        });
        this.componentList = [];
        this.sliderRef.clear();
    }

    initializeVariables() {
        this.componentList = [];
        this.pages = [];
        this.currentPage = 0;
        this.leftMarginMove = 0;

        this.itemsToShow = this.options.itemsToShow[this.screenType];
        this.itemsToMove = this.options.itemsToMove[this.screenType];
        this.dragToMove = this.options.dragToMove[this.screenType];
        this.fixedWidth = this.options.fixedWidth?.[this.screenType];
        this.containerWidth = this.options.containerWidth?.[this.screenType];

        this.arrowsClasses = {
            '-inner-container': !!this.options?.arrowsInnerContainer
        };
        this.useExtendedArrows = this.getIsTouchDevice() || !!this.options?.useExtendedArrows?.[this.screenType];
        const arrowsSizeClasses = `eva-3-nav-slider ${this.options?.arrowsSize || '-sm'}`;
        const arrowsColorClass = `-eva-3-shadow-line-hover ${this.options?.arrowsColor?.[this.screenType] || '-white'}`;

        this.arrowClasses = `${arrowsSizeClasses} ${arrowsColorClass} ${this.options?.arrowsInnerContainer? ' -inner-container' : ''}`;
        
        this.arrowContainerClasses = `${arrowsSizeClasses} ${this.useExtendedArrows ? 'extended-arrow-container' : arrowsColorClass}`;

        this.checkInfiniteMode();
        this.checkAutoScroll();
        this.calculateArrowPositions();
    }

    private checkInfiniteMode(): void {
        this.virtualCurrentIndex = 0;
        this.originalComponentsLength = this.components ? this.components.length : 0;
        
        this.isInfiniteMode = !!(this.options.infiniteScroll?.[this.screenType]) && 
            this.components && 
            this.components.length > this.itemsToShow &&
            this.itemsToMove > 0 && 
            this.itemsToMove <= this.originalComponentsLength;
    }

    private checkAutoScroll() {
        if (this.isInfiniteMode &&
            this.options.autoScroll?.[this.screenType] &&
            isPlatformBrowser(this.platformId)) {
            this.initIntersectionObserver();
        }
    }

    private initIntersectionObserver() {
        this.observer = new IntersectionObserver(
            (entries) => {
                if (entries.some(entry => entry.isIntersecting)) {
                    this.startAutoScroll();
                } else {
                    this.stopAutoScroll();
                }
            },
            { threshold: 0.5 }
        );
        this.observer.observe(this.sliderPro.element.nativeElement);
    }

    private startAutoScroll() {
        if (!this.options.autoScroll?.[this.screenType] || 
            this.autoScrollIntervalId || 
            this.isHovered) {
            return;
        }

        this.autoScrollIntervalId = setInterval(() => {
            this.nextInfinite(true);
        }, this.options.autoScrollInterval?.[this.screenType] || SliderProComponent.DEFAULT_AUTO_SCROLL_INTERVAL);
    }

    private stopAutoScroll() {
        if (this.autoScrollIntervalId) {
            clearInterval(this.autoScrollIntervalId);
            this.autoScrollIntervalId = null;
        }
    }

    private pauseAutoScrollOnInteraction() {
        if (this.autoScrollIntervalId) {
            this.stopAutoScroll();
            this.scheduleAutoScrollResume();
        }
    }

    private scheduleAutoScrollResume(delay: number = SliderProComponent.INTERACTION_PAUSE_DURATION, callback?: () => void) {
        this.clearHoverTimeout();
        this.hoverTimeoutId = setTimeout(() => {
            callback?.();
            this.startAutoScroll();
            this.hoverTimeoutId = null;
        }, delay);
    }

    private stopAutoScrollAndCleanup() {
        this.stopAutoScroll();
        this.clearHoverTimeout();
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    private clearHoverTimeout(): void {
        if (this.hoverTimeoutId) {
            clearTimeout(this.hoverTimeoutId);
            this.hoverTimeoutId = null;
        }
    }

    initialize() {
        this.componentList = [];
        this.currentPage = 0;
        this.virtualCurrentIndex = 0;
        this.leftMarginMove = 0;
        this.isTouchDevice = this.getIsTouchDevice();

        if (this.isInfiniteMode) {
            this.initializeInfinite();
        } else {
            this.initializeNormal();
        }
    }

    private initializeNormal(): void {
        this.calculateItemDimensions();
        this.calculateRowWidths();

        this.maxLeftMarginMove = (this.totalRowWidth - (this.itemWidthValue * this.itemsToShow)) * -1;

        const pagesAmount = Math.ceil((this.components.length - this.itemsToShow) / this.itemsToMove) + 1;
        this.pages = pagesAmount > 0 ? Array(pagesAmount).fill('') : [];

        for (let i = 0; i < this.components.length; i++) {
            if (this.totalItemsToDraw > i) {
                this.createComponent(i);
            }
        }
    }

    private initializeInfinite(): void {
        this.calculateItemDimensions();
        this.calculateRowWidths();

        const pagesAmount = this.components.length;
        this.pages = pagesAmount > 0 ? Array(pagesAmount).fill('') : [];
        this.currentPage = 0;

        for (let i = 0; i < this.components.length; i++) {
            this.createComponent(i);
        }
    }

    private calculateItemDimensions(): void {
       
        const totalInnerGap = this.shouldUseGaps() ? 
            SliderProComponent.MARGIN_GAP * Math.ceil((this.itemsToShow - 1)) : 0;
        
        let availableWidth = (this.sliderProWidth - totalInnerGap);
        
        if (this.currentScreenType == 'MOBILE' && !this.viewFullWidth) {
            if (this.useGaps) {
                availableWidth -= SliderProComponent.MARGIN_GAP;
            }
        }

        if (this.fixedWidth == null) {
            this.itemWidthValue = this.isInfiniteMode ? 
                Math.floor(availableWidth / this.itemsToShow) : 
                availableWidth / this.itemsToShow;
                
            if (this.currentScreenType == 'MOBILE' && this.itemsToShow == 1 && !this.viewFullWidth) {
                if (this.useGaps) {
                    this.itemWidthValue -= SliderProComponent.MARGIN_GAP;
                }
            }
        } else {
            this.itemWidthValue = this.fixedWidth;
            this.itemsToShow = Math.floor(availableWidth / this.fixedWidth);
        }
    }

    private calculateRowWidths(): void {
        const gapSize = this.shouldUseGaps() ? SliderProComponent.MARGIN_GAP : 0;

        this.totalItemsToDraw = this.isInfiniteMode ? this.components.length : Math.ceil(this.itemsToShow) + this.itemsToMove;

        this.parcialRowWidth = (this.itemWidthValue * this.totalItemsToDraw) + 
                            (gapSize * (this.totalItemsToDraw - 1));

        if (this.currentScreenType === 'MOBILE' && !this.viewFullWidth) {
            this.parcialRowWidth += SliderProComponent.MARGIN_GAP * 2;
        }

        this.totalRowWidth = (this.itemWidthValue * this.components.length) + 
                            (gapSize * (this.components.length - 1));
    }

    private shouldUseGaps(): boolean {
        return !this.viewFullWidth && this.useGaps;
    }

    private calculateArrowPositions(): void {
        if (!this.viewFullWidth || !isPlatformBrowser(this.platformId) || this.currentScreenType === 'MOBILE') {
            this.arrowLeftPosition = null;
            this.arrowRightPosition = null;
            return;
        }
        const screenWidth = globalThis.innerWidth || 0;
        const defaultPosition = 43;

        const finalPosition = screenWidth > 1280 
            ? (screenWidth - 1280) / 2 
            : defaultPosition;
            
        const positionValue = `${finalPosition}px`;
        this.arrowLeftPosition = positionValue;
        this.arrowRightPosition = positionValue;
    }

    private calculateMoveDistance(): number {
        // viewFullWidth + infinite: sin gaps en navegación
        return this.shouldUseGaps() ? 
        (this.itemWidthValue + SliderProComponent.MARGIN_GAP) : 
        this.itemWidthValue;
    }

    createComponent(index: number) {
        if (this.componentList[index] == null) {
            const componentRef = this.sliderRef.createComponent(this.components[index]);
            this.componentList[index] = componentRef;
            const additionalClass = this.componentParams[index]?.hostCssClass;
            componentRef.location.nativeElement.setAttribute("class", "slider-pro-item" + (additionalClass ? ` ${additionalClass}` : ''));
            Object.assign(componentRef.instance, this.componentParams[index]);
        }
    }

    prev() {
        this.pauseAutoScrollOnInteraction();
        if (this.isInfiniteMode) {
            this.prevInfinite();
        } else {
            this.prevNormal();
        }
    }

    next() {
        this.pauseAutoScrollOnInteraction();
        if (this.isInfiniteMode) {
            this.nextInfinite();
        } else {
            this.nextNormal();
        }
    }

    private prevNormal() {
        if ((this.currentPage - 1) >= 0) {
            this.currentPage -= 1;
            
            const moveDistance = this.calculateMoveDistance();
            const possibleMarginLeftMove = this.leftMarginMove + (moveDistance * this.itemsToMove);
            this.leftMarginMove = Math.min(0, possibleMarginLeftMove);

            this.upaService.trackClickTopic({...this.upaData, ...{ label: 'left-arrow', target: UpaTarget.SLIDER } });
        }
    }

    private nextNormal() {
        if ((this.currentPage + 1) < this.pages.length) {
            this.currentPage += 1;

            const moveDistance = this.calculateMoveDistance();
            const possibleMarginLeftMove = this.leftMarginMove - (moveDistance * this.itemsToMove);
            this.leftMarginMove = Math.max(this.maxLeftMarginMove, possibleMarginLeftMove);

            const index = Math.floor(this.itemsToShow + this.itemsToMove + this.currentPage - 1);

            for (let i = 0; i < this.itemsToMove; i++) {
                const currentIndex = index + i;
                if (this.components[currentIndex] != null && this.componentList[currentIndex] == null) {
                    this.parcialRowWidth += (moveDistance * this.itemsToMove);
                    this.createComponent(currentIndex);
                }
            }

            this.upaService.trackClickTopic({...this.upaData, ...{ label: 'right-arrow', target: UpaTarget.SLIDER } });
        }
    }

    private prevInfinite(): void {
        const maxValidPosition = this.originalComponentsLength - this.itemsToShow;

        let newCurrentPage: number;
        let newVirtualCurrentIndex: number;

        if ((this.currentPage - this.itemsToMove) < 0) {
            if (this.itemsToShow === 1 && this.itemsToMove === 1) {
                this.virtualPositionElement();
            } else {
                newCurrentPage = maxValidPosition;
                while (newCurrentPage > 0 && (newCurrentPage % this.itemsToMove !== 0)) {
                    newCurrentPage--;
                }
                newVirtualCurrentIndex = newCurrentPage;
                this.moveInfiniteDefault(newCurrentPage, newVirtualCurrentIndex);
            }
        } else {
            newCurrentPage = this.currentPage - this.itemsToMove;
            newVirtualCurrentIndex = this.virtualCurrentIndex - this.itemsToMove;

            this.moveInfiniteDefault(newCurrentPage, newVirtualCurrentIndex);
        }

        this.upaService.trackClickTopic({...this.upaData, ...{ label: 'left-arrow', target: UpaTarget.SLIDER } });
    }

    private nextInfinite(disableTracking?: boolean): void {
        const maxValidPosition = this.originalComponentsLength - this.itemsToShow;

        if ((this.currentPage + this.itemsToMove) > maxValidPosition) {
            if (this.itemsToShow === 1 && this.itemsToMove === 1) {
                this.virtualPositionElement();
            } else {
                this.moveInfiniteDefault(0, 0);
            }
        } else {
            const newCurrentPage = this.currentPage + this.itemsToMove;
            const newVirtualCurrentIndex = this.virtualCurrentIndex + this.itemsToMove;

            this.moveInfiniteDefault(newCurrentPage, newVirtualCurrentIndex);
        }

        if (!disableTracking) {
            this.upaService.trackClickTopic({...this.upaData, ...{ label: 'right-arrow', target: UpaTarget.SLIDER } });
        }
    }

    moveInfiniteDefault(newCurrentPage: number, newVirtualCurrentIndex: number): void {
        const moveDistance = this.calculateMoveDistance();
        const newLeftMarginMove = -1 * newVirtualCurrentIndex * moveDistance;

        this.currentPage = newCurrentPage;
        this.virtualCurrentIndex = newVirtualCurrentIndex;
        this.leftMarginMove = newLeftMarginMove;
    }

    moveVirtualElement(elementIndex: number, x: number): void {
        const elementRef = this.componentList[elementIndex];
        if (elementRef && elementRef.location && elementRef.location.nativeElement) {
            const el = elementRef.location.nativeElement;
            el.style.transition = 'none';
            el.style.transform = `translate3d(${x}px, 0, 0)`;
        }
    }

    moveVirtualContainer(direction: 'next' | 'prev'): void {
        const container = this.sliderPro?.element?.nativeElement?.querySelector('.slider-pro-container');
        if (container) {
            const onTransitionEnd = () => {
                this.moveVirtualElement(direction === 'next' ? 0 : this.originalComponentsLength - 1, 0);
                container.style.transition = 'none';
                this.leftMarginMove = direction === 'next' ? 0 : -1 * (this.originalComponentsLength - 1) * this.calculateMoveDistance();
                this.currentPage = direction === 'next' ? 0 : this.pages.length - 1;
                this.virtualCurrentIndex = direction === 'next' ? 0 : this.originalComponentsLength - 1;

                setTimeout(() => {
                    container.style.transition = 'transform 500ms ease';
                }, 10);
            };
            container.addEventListener('transitionend', onTransitionEnd, { once: true });
        }
    }

    virtualPositionElement(): void {
        if (
            this.sliderRef &&
            this.sliderRef.length
        ) {
            if (this.currentPage === this.pages.length - 1) {
                this.moveVirtualElement(0, this.originalComponentsLength * this.calculateMoveDistance());
                this.leftMarginMove = -1 * this.originalComponentsLength * this.calculateMoveDistance();
                this.moveVirtualContainer('next');
            } else if (this.currentPage === 0) {
                this.moveVirtualElement(this.originalComponentsLength - 1, -1 * this.originalComponentsLength * this.calculateMoveDistance());
                this.leftMarginMove = this.calculateMoveDistance();
                this.moveVirtualContainer('prev');
            }
        }
    }

    // Events for mobile
    onTouchStart(event: TouchEvent) {
        this.pauseAutoScrollOnInteraction();
        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
    }

    onTouchMove(event: TouchEvent) {

        const currentX = event.touches[0].clientX;
        const currentY = event.touches[0].clientY;

        const deltaX = currentX - this.startX;
        const deltaY = currentY - this.startY;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            event.preventDefault();
            if (deltaX > 0) {
                this.actionType = 'prev';
            } else {
                this.actionType = 'next';
            }
        } else {
            this.actionType = null;
        }
    }

    onTouchEnd() {
        if (this.actionType) {
            this.actionType == 'next' ? this.next() : this.prev();
        }
    }

    // Events for desktop
    onMouseDown(event: MouseEvent) {
        this.pauseAutoScrollOnInteraction();
        this.startDrag = true;
        this.startX = event.clientX;
        this.startY = event.clientY;
    }

    onMouseMove = (event: MouseEvent) => {
        if (this.startDrag) {
            const currentX = event.clientX;
            const currentY = event.clientY;
            const deltaX = currentX - this.startX;
            const deltaY = currentY - this.startY;

            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                event.preventDefault();
                if (deltaX > 0) {
                    this.actionType = 'prev';
                } else {
                    this.actionType = 'next';
                }
            } else {
                this.actionType = null;
            }
        }
    }

    onMouseUp = () => {
        this.startDrag = false;
        if (this.actionType) {
            this.actionType == 'next' ? this.next() : this.prev()
        }
    }

    handleMouseEnter(): void {
        this.isHovered = true;
        this.clearHoverTimeout();
        this.stopAutoScroll();
    }

    handleMouseLeave(): void {
        this.scheduleAutoScrollResume(200, () => {
            this.isHovered = false;
        });
    }
}
